import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import {
  Button,
  CircularProgress,
  FormHelperText,
  Icon,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Sparkles } from "lucide-react";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import BudgetRecommendationInfo from "@/features/campaigns/components/budgetRecommendation/BudgetRecommendationInfo";
import { CAMPAIGN_CONST } from "@/config/constants";
import {
  EstimatedImpressionsDisclosure,
  EstimatedImpressionsVisibilityProvider,
  EstimatedImpressionsText,
} from "@/features/campaigns/shared/components/EstimatedImpressions";
import type { EstimatedImpressionsResult } from "@/features/campaigns/shared/helpers/estimatedImpressionsHelper";

type BudgetValue = string | number | null | undefined;

type BudgetRecommendation = Record<string, unknown> & {
  finalRecommendation?: number | string | null;
  explanation?: string | null;
};

type LocationBudgetProps = {
  budget?: BudgetValue;
  budgetRecommendation?: BudgetRecommendation | null;
  marketData?: Record<string, unknown> | null;
  minMaxRequired?: {
    minRequiredBudget: number | string;
    maxRequiredBudget: number | string;
  } | null;
  onEnterBudget: (_value: number | undefined) => void;
  onGetRecommendation?: () => void;
  canGetRecommendation?: boolean;
  isGettingRecommendation?: boolean;
  recommendationError?: string | null;
  campaignType?: string;
  isBudgetRecommendationAvailable?: boolean;
  initialDisabled?: boolean;
  estimatedImpressions?: EstimatedImpressionsResult | null;
  impressionEstimationEnabled?: boolean;
  hasSelectedPackage?: boolean;
  hasSelectedChannel?: boolean;
};

const RECOMMENDED_BUDGET_BLUE = "#0a58b5";

function parseBudgetValue(budget: BudgetValue): number | "" {
  const numericBudget = Number.parseFloat(String(budget ?? ""));
  return budget || numericBudget === 0 ? numericBudget : "";
}

export default function LocationBudget({
  budget = "",
  budgetRecommendation,
  marketData,
  minMaxRequired,
  onEnterBudget,
  onGetRecommendation = () => {},
  canGetRecommendation = false,
  isGettingRecommendation = false,
  recommendationError,
  campaignType,
  isBudgetRecommendationAvailable = false,
  initialDisabled,
  estimatedImpressions,
  impressionEstimationEnabled = false,
  hasSelectedPackage = true,
  hasSelectedChannel = hasSelectedPackage,
}: LocationBudgetProps) {
  const [isFocused, setIsFocused] = useState(false);
  const isLocationSpecificBudget =
    campaignType === CAMPAIGN_CONST.BUDGET_TYPE.LOCATION_SPECIFIC;

  const [value, setValue] = useState<number | "">(() =>
    parseBudgetValue(budget)
  );

  useEffect(() => {
    setValue(parseBudgetValue(budget));
  }, [budget]);

  const handleChange = (nextValue: number | undefined) => {
    setValue(nextValue ?? "");
    onEnterBudget(nextValue);
  };

  const renderHelperText = Boolean(minMaxRequired && value !== "" && isFocused);
  const helperText = recommendationError
    ? recommendationError
    : renderHelperText
      ? `Please enter a budget between $${minMaxRequired?.minRequiredBudget} and $${minMaxRequired?.maxRequiredBudget}`
      : "";
  const isBudgetInputDisabled = initialDisabled || !isLocationSpecificBudget;
  const isRecommendationButtonDisabled =
    initialDisabled || !canGetRecommendation || isGettingRecommendation;
  const hasBudgetRecommendation =
    isBudgetRecommendationAvailable && Boolean(budgetRecommendation);
  const canShowEstimatedImpressions =
    impressionEstimationEnabled &&
    hasSelectedPackage &&
    hasSelectedChannel &&
    isLocationSpecificBudget &&
    Boolean(value) &&
    !minMaxRequired;

  return (
    <EstimatedImpressionsVisibilityProvider>
      <div className="location-budget">
        {hasBudgetRecommendation ? (
          <BudgetRecommendationInfo
            recommendation={budgetRecommendation}
            marketData={marketData}
            label="Recommended Budget"
          />
        ) : (
          <label>Location Budget</label>
        )}
        <div
          className="location-budget-control-row"
          data-testid="location-budget-control-row"
        >
          <NumericFormat
            customInput={TextField}
            onFocus={() => setIsFocused(true)}
            value={value}
            onValueChange={(values) => handleChange(values.floatValue)}
            name="locationBudget"
            id="locationBudget"
            thousandSeparator
            decimalScale={2}
            allowNegative={false}
            isAllowed={({ floatValue }) =>
              floatValue === undefined || floatValue <= 1000000
            }
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon
                      component={RpSvgIcon}
                      name="money1"
                      color="primary"
                      style={
                        hasBudgetRecommendation
                          ? { color: RECOMMENDED_BUDGET_BLUE }
                          : undefined
                      }
                    />
                  </InputAdornment>
                ),
              },
            }}
            variant="outlined"
            size="small"
            autoComplete="off"
            error={
              Boolean(recommendationError) ||
              Boolean(minMaxRequired && value !== "")
            }
            placeholder="0.00"
            disabled={isBudgetInputDisabled}
          />
          {isBudgetRecommendationAvailable && !hasBudgetRecommendation ? (
            <Button
              type="button"
              variant="outlined"
              className="budget-recommendation-button"
              startIcon={
                isGettingRecommendation ? (
                  <CircularProgress color="inherit" size={14} thickness={5} />
                ) : (
                  <Sparkles size={16} strokeWidth={1.8} />
                )
              }
              disabled={isRecommendationButtonDisabled}
              onClick={onGetRecommendation}
            >
              {isGettingRecommendation
                ? "In Progress..."
                : "Get Recommendation"}
            </Button>
          ) : null}
        </div>

        {helperText ? (
          <FormHelperText
            className="fade-in"
            error={Boolean(recommendationError)}
          >
            {helperText}
          </FormHelperText>
        ) : null}
        {impressionEstimationEnabled &&
          !hasSelectedChannel &&
          Number(value) > 0 &&
          !minMaxRequired &&
          isLocationSpecificBudget && (
            <Typography
              variant="caption"
              className="d-block mt-1"
              sx={{
                color: "var(--neutral-600)",
                fontFamily: "Open Sans, sans-serif",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "19.92px",
                wordBreak: "break-word",
              }}
            >
              A Creative needs to be selected to show Est. Impressions
            </Typography>
          )}
        <EstimatedImpressionsText
          enabled={canShowEstimatedImpressions}
          estimate={canShowEstimatedImpressions ? estimatedImpressions : null}
        />
        {canShowEstimatedImpressions ? (
          <EstimatedImpressionsDisclosure enabled />
        ) : null}
      </div>
    </EstimatedImpressionsVisibilityProvider>
  );
}
